/**
 * Générateur — Automatisme : reconnaître des solides (pavé droit, cube, prisme
 * droit, cylindre, pyramide, cône).
 * Attendu ID : 1238 | Niveau : 3ème | Domaine : Espace et géométrie,
 * Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1072-1073 « Reconnaitre
 * des solides… »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1019 (automatisme 6e l. 1595, sept solides, primitives
 * homogènes, solveur figure-based) : les CIBLES sont les six solides du
 * programme de 3e, la boule ne sert que de distracteur (À ARBITRER) ; le
 * verdict « neuf d'assemblage 946 + 185 » du mapping est écarté : les SVG de
 * 178 / 185 portent une hauteur rouge et un « S » qui trahiraient pyramide
 * et cône dans une planche. Pas de fiche (règle 9 : format de pur rappel).
 * Comme en 6e : identifier seulement, pas de comptage de faces ni
 * d'inclusion questionnée (arbitrage prof 2026-08-22), arêtes cachées en
 * pointillés, vues canoniques.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Nommer le solide » (nommer_qcm_unique) : un solide, « Quel est
 *     le nom le plus précis de ce solide ? », quatre noms (1019 F1).
 *   Carte 2 « Trouver le solide » (trouver_qcm_unique) : planche 2 × 2,
 *     « Lequel de ces solides est un cône ? » (1019 F2).
 *   Carte 3 « Relier » (relier_relier) : planche 2 × 2 numérotée, chaque
 *     figure à son nom ; jamais deux de {cube, pavé, prisme} ensemble.
 *   Carte 4 « Vrai ou faux » (sens_tableau_vf) : planche 2 × 2, « La figure
 *     ② est un cône. » ; jamais une inclusion vraie (un cube nommé pavé).
 *
 * INVARIANTS : verrous d'inclusion de 1019 (cible pavé → jamais de cube dans
 * la planche ; cible prisme → jamais de cube ni de pavé) ; planches à classes
 * toutes différentes en relier et V/F ; le solveur RECLASSE chaque figure
 * depuis ses primitives. Pas de sacrifice (pas de fiche).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   pointu        : cône ↔ pyramide, PRINCIPALE ;
 *   triangle_vu   : prisme triangulaire ↔ pyramide ;
 *   rond          : cylindre ↔ cône, boule ;
 *   inclusion     : cube ↔ pavé ↔ prisme (jamais servie contre le cas général).
 */
import {
    randomChoice, randomChoiceMultiple, shuffle,
    makeQcm, makeVraiFaux, makeRelier,
} from '../../../utils';
import {
    SOLIDES, bboxDe, emettre, svgPlanche, NOMS, CONFUSIONS, EXCLUS, NUMS, CASES4, dataUrl, elImage,
} from '../../6eme/geometrie/gen_1019';

const ATTENDU_ID = 1238;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NOMMER = 'Nommer le solide';
const L_TROUVER = 'Trouver le solide';
const L_RELIER = 'Relier';
const L_VF = 'Vrai ou faux';

/** Les six cibles du programme de 3e ; la boule reste un distracteur. */
export const CIBLES = ['cube', 'pave', 'pyramide', 'prisme', 'cylindre', 'cone'];
const TOUS = [...CIBLES, 'boule'];
const BOITES = ['cube', 'pave', 'prisme'];

const CODE = { cone: 'pointu', pyramide: 'pointu', prisme: 'triangle_vu', cylindre: 'rond', boule: 'rond', cube: 'inclusion', pave: 'inclusion' };
const EXPLICATIONS = {
    regle: 'Une pointe : pyramide (faces triangulaires planes) ou cône (base ronde). Pas de pointe : pavé, cube, prisme (des faces planes) ou cylindre (deux disques).',
    pointu: 'Le cône a une base ronde et une surface courbe ; la pyramide a des faces triangulaires planes et des arêtes.',
    triangle_vu: 'Le prisme droit a DEUX bases triangulaires pareilles et des faces rectangulaires ; la pyramide n\'a qu\'une base et une pointe.',
    rond: 'Le cylindre a deux disques pareils ; le cône n\'en a qu\'un et une pointe ; la boule n\'a aucune face plane.',
    inclusion: 'Le cube a six faces carrées ; le pavé droit a des faces rectangulaires ; le prisme droit a deux bases qui ne sont pas des rectangles.',
};

const planche4 = (types) => {
    const cases = [...CASES4]; cases.cw = 156; cases.ch = 116;
    return svgPlanche(types, cases, 312, 232, 300, 116, 92);
};

// ─── Carte 1 : nommer le solide ───────────────────────────────────────────

const genNommer = () => {
    const type = randomChoice(CIBLES);
    const parts = SOLIDES[type]();
    const bb = bboxDe(parts);
    const W = Math.ceil(bb.maxX - bb.minX) + 28; const H = Math.ceil(bb.maxY - bb.minY) + 28;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${Math.min(220, W * 1.6)}" role="img" aria-label="Un solide dessiné en perspective, arêtes cachées en pointillés.">\n  <g data-fig="0">\n  ${emettre(parts, 14 - bb.minX, 14 - bb.minY, 1).join('\n  ')}\n  </g>\n</svg>`;
    const dis = CONFUSIONS[type].map((t) => ({ valeur: NOMS[t], code: CODE[t] }));
    const payload = makeQcm([NOMS[type], ...dis.map((d) => d.valeur)], NOMS[type], '<p><b>Quel est le nom le plus précis de ce solide ?</b></p><p>{{element:0}}</p>', { ...opts(L_NOMMER), elements_additionnels: elImage(dataUrl(svg)) });
    payload.contenu = { ...payload.contenu, sousType: type, explications: EXPLICATIONS };
    payload._meta.params = { type, distracteurs: dis.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : trouver le solide (planche 2 × 2) ─────────────────────────

const genTrouver = () => {
    const cible = randomChoice(CIBLES);
    const interdits = [cible, ...(EXCLUS[cible] || [])];
    const autres = randomChoiceMultiple(TOUS.filter((t) => !interdits.includes(t)), 3);
    const types = shuffle([cible, ...autres]);
    const idx = types.indexOf(cible);
    const dis = types.map((t, k) => ({ t, k })).filter(({ k }) => k !== idx).map(({ t, k }) => ({ valeur: `Figure ${NUMS[k]}`, code: CODE[t] }));
    const payload = makeQcm([`Figure ${NUMS[idx]}`, ...dis.map((d) => d.valeur)], `Figure ${NUMS[idx]}`, `<p><b>Lequel de ces solides est ${NOMS[cible]} ?</b></p><p>{{element:0}}</p>`, { ...opts(L_TROUVER), elements_additionnels: elImage(dataUrl(planche4(types))) });
    payload.contenu = { ...payload.contenu, sousType: cible, explications: EXPLICATIONS };
    payload._meta.params = { cible, types, distracteurs: dis.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 3 : relier chaque figure à son nom ────────────────────────────

/** Quatre classes toutes différentes, une boîte (cube / pavé / prisme) au plus. */
const tirerPlancheDistincte = () => {
    const boite = randomChoice(BOITES);
    const types = randomChoiceMultiple([boite, 'pyramide', 'cylindre', 'cone', 'boule'], 4);
    return shuffle(types);
};

const genRelier = () => {
    const types = tirerPlancheDistincte();
    const pairs = types.map((t, k) => ({ source: `Figure ${NUMS[k]}`, target: NOMS[t] }));
    const payload = makeRelier(pairs, '<p><b>Relie chaque figure au nom du solide.</b></p><p>{{element:0}}</p>', { ...opts(L_RELIER), elements_additionnels: elImage(dataUrl(planche4(types))) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { types };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

/** Une inclusion vraie (un cube nommé pavé ou prisme, un pavé nommé prisme) n'est jamais servie. */
const inclut = (nom, type) => (nom === 'pave' && type === 'cube') || (nom === 'prisme' && BOITES.includes(type) && type !== 'prisme');

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const types = shuffle(randomChoiceMultiple(TOUS, 4));
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = types.map((t, k) => {
            if (verites[k]) return { ligne: `La figure ${NUMS[k]} est ${NOMS[t]}.`, code: null };
            const faux = CIBLES.filter((n) => n !== t && !inclut(n, t));
            if (!faux.length) return null;
            const n = randomChoice(faux);
            return { ligne: `La figure ${NUMS[k]} est ${NOMS[n]}.`, code: CODE[n] };
        });
        if (lignes.some((l) => !l)) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p><p>{{element:0}}</p>', { ...opts(L_VF), elements_additionnels: elImage(dataUrl(planche4(types))) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { types, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1238 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'six cibles (cube, pavé droit, prisme droit, cylindre, pyramide, cône), la boule en distracteur seulement ; identifier seulement, vues canoniques de 1019',
        'verrous d\'inclusion de 1019 (pavé → pas de cube ; prisme → ni cube ni pavé) ; en relier et V/F, une inclusion vraie n\'est jamais servie',
    ],
    nommer_qcm_unique: 'les confusions voisines de 1019 (cône ↔ pyramide pointu, prisme ↔ pyramide triangle_vu, cylindre ↔ cône ↔ boule rond, cube ↔ pavé ↔ prisme inclusion)',
    trouver_qcm_unique: 'les trois autres figures de la planche, classes différentes de la cible',
    sens_tableau_vf: 'un nom faux par ligne fausse, parmi les six cibles, hors inclusion vraie',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    nommer_qcm_unique: genNommer,
    trouver_qcm_unique: genTrouver,
    relier_relier: genRelier,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'nommer', label: L_NOMMER, formatsList: [{ key: 'nommer_qcm_unique', label: 'QCM' }] },
    { id: 'trouver', label: L_TROUVER, formatsList: [{ key: 'trouver_qcm_unique', label: 'QCM' }] },
    { id: 'relier', label: L_RELIER, formatsList: [{ key: 'relier_relier', label: 'Relier' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
